import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const NAV = [
  { id: "services", label: "Services" },
  { id: "why-nidan", label: "Why Nidan" },
  { id: "how-it-works", label: "How It Works" },
  { id: "appointment", label: "Book" },
  { id: "testimonials", label: "Reviews" },
  { id: "visit-us", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px] md:h-[84px]">
          <a
            href="#home"
            className="flex items-center gap-2 shrink-0"
            data-testid="header-logo"
            aria-label="Nidan Diagnostic Centre — Home"
          >
            <img
              src="/assets/logo-nidan.jpeg"
              alt="Nidan Diagnostic Centre logo"
              className="h-12 md:h-14 w-auto object-contain"
              loading="eager"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-testid={`nav-${n.id}`}
                className="text-sm font-medium text-slate-600 hover:text-[#0099CC] transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+918854865454"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#0099CC]"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              8854865454
            </a>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#00BFFF] hover:bg-[#0099CC] text-white font-semibold py-2.5 px-5 rounded-full transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              Book on WhatsApp
            </a>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-slate-700 hover:bg-slate-100"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4 border-t border-slate-100" data-testid="mobile-menu">
            <div className="grid gap-1 pt-3">
              {NAV.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="px-3 py-2.5 rounded-lg text-base font-medium text-slate-700 hover:bg-slate-50"
                >
                  {n.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-3">
                <a
                  href="tel:+918854865454"
                  className="px-3 py-2.5 rounded-lg text-base font-semibold text-slate-700 bg-slate-50"
                  data-testid="mobile-call-btn"
                >
                  Call 8854865454
                </a>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="text-center bg-[#00BFFF] hover:bg-[#0099CC] text-white font-semibold py-2.5 px-5 rounded-full"
                >
                  Book on WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
